import { Stack } from '@mui/system';
import { compact } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Settings04Icon } from '@/components/icons/Settings04Icon';
import { BigBreadcrumbs } from '@/components/navigation/BigBreadcrumbs';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  BalanceSheetConfigPanel,
  UpdateConfigurationFn,
} from '@/modules/balanceSheet/BalanceSheetConfigPanel/BalanceSheetConfigPanel';
import { useUpdateBalanceSheetViewConfigurationMutation } from '@/modules/balanceSheet/BalanceSheetConfigurationPopper/graphql/BalanceSheetConfigurationPopper.generated';
import { BalanceSheetExportDropdown } from '@/modules/balanceSheet/BalanceSheetExportDropdown/BalanceSheetExportDropdown';
import { AssociatedEntitiesModal } from '@/modules/balanceSheet/BalanceSheetTable/AssociatedEntitiesModal/AssociatedEntitiesModal';
import { BalanceSheetTable } from '@/modules/balanceSheet/BalanceSheetTable/BalanceSheetTable';
import {
  BalanceSheetTableProvider,
  useBalanceSheetTableContext,
} from '@/modules/balanceSheet/BalanceSheetTable/BalanceSheetTableContext';
import { HouseholdEstateValueMetrics } from '@/modules/household/HouseholdEstateValueMetrics/HouseholdEstateValueMetrics';
import { EditLiabilityModal } from '@/modules/liabilities/LiabilityModal/EditLiabilityModal';
import { CustomerThemeProvider } from '@/styles/themes/CustomerThemeProvider';

import {
  BalanceSheetDocument,
  useBalanceSheetQuery,
} from './graphql/BalanceSheet.generated';

interface ClientBalanceSheetPageInnerProps {
  loading: boolean;
  refetch: () => void;
  data: ReturnType<typeof useBalanceSheetQuery>['data'];
  householdDisplayName: string;
}

function ClientBalanceSheetPageInner({
  loading,
  data,
  refetch,
  householdDisplayName,
}: ClientBalanceSheetPageInnerProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const canEdit = useCanEditHouseholdData();
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
  const {
    selectedEntitiesRow,
    setSelectedEntitiesRow,
    selectedLiabilityRow,
    setSelectedLiabilityRow,
    renderWhiteLabeled,
    viewConfiguration,
  } = useBalanceSheetTableContext();

  const [updateBalanceSheet] = useUpdateBalanceSheetViewConfigurationMutation({
    onError: (error) => {
      reportError('Error updating balance sheet configuration', error);
      showFeedback(
        'Error updating the balance sheet configuration. Please refresh the page and try again.'
      );
    },
  });

  const updateConfiguration: UpdateConfigurationFn = useCallback(
    ({ id, update, refetchQueries, awaitRefetchQueries }) => {
      if (!id) return Promise.resolve({});
      return updateBalanceSheet({
        variables: { input: { id, update } },
        refetchQueries,
        awaitRefetchQueries,
      });
    },
    [updateBalanceSheet]
  );
  const refetchDocuments = useMemo(() => [BalanceSheetDocument], []);

  const estateValueMetrics =
    data?.node?.__typename === 'Household'
      ? data.node.estateValueMetrics
      : null;
  const householdAssetsSummary = data?.householdAssetsSummary ?? null;
  // Hide the summary metrics when there's no data so the empty state centers in
  // the full region rather than sitting below a row of all-$0 metric cards.
  const hasNoValues = !!data?.balanceSheet?.hasNoValues;
  const showSummaryMetrics =
    (viewConfiguration?.showSummaryMetrics ?? false) && !hasNoValues;

  const handleCloseLiabilityModal = useCallback(() => {
    refetch();
    setSelectedLiabilityRow(null);
  }, [refetch, setSelectedLiabilityRow]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        pb={2}
      >
        <Stack
          direction="row"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
        >
          <BigBreadcrumbs
            crumbs={compact([
              {
                label: 'Balance sheet',
              },
            ])}
          />
          <Stack spacing={1} direction="row" alignItems="center">
            <BalanceSheetExportDropdown
              disabled={!data?.balanceSheet || !!data.balanceSheet.hasNoValues}
            />
            {canEdit && (
              <Button
                variant="secondary"
                size="sm"
                startIcon={Settings04Icon}
                disabled={
                  !data?.balanceSheet || !!data.balanceSheet.hasNoValues
                }
                onClick={() => setIsConfigPanelOpen(true)}
              >
                Configure view
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack spacing={2} flex={1} minHeight={0}>
        {showSummaryMetrics && (
          <HouseholdEstateValueMetrics
            metrics={estateValueMetrics}
            assetsSummary={householdAssetsSummary}
          />
        )}
        <Stack flex={1} minHeight={0} sx={{ overflow: 'auto' }}>
          <CustomerThemeProvider forceLuminaryTheme={!renderWhiteLabeled}>
            <BalanceSheetTable
              loading={loading}
              balanceSheet={data?.balanceSheet ?? null}
            />
          </CustomerThemeProvider>
          {selectedEntitiesRow && (
            <AssociatedEntitiesModal
              isOpen={!!selectedEntitiesRow}
              onClose={() => setSelectedEntitiesRow(null)}
              entityIds={selectedEntitiesRow.entityIds ?? []}
            />
          )}
          {selectedLiabilityRow && (
            <EditLiabilityModal
              isOpen={!!selectedLiabilityRow}
              onClose={handleCloseLiabilityModal}
              liabilityId={selectedLiabilityRow.liabilityId}
            />
          )}
        </Stack>
      </Stack>
      <BalanceSheetConfigPanel
        open={isConfigPanelOpen}
        onClose={() => setIsConfigPanelOpen(false)}
        balanceSheet={data?.balanceSheet ?? null}
        updateConfiguration={updateConfiguration}
        refetchDocuments={refetchDocuments}
        householdName={householdDisplayName}
      />
    </>
  );
}

export function ClientEstateBalanceSheetPage() {
  const householdId = useRequiredParam('householdId');
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const { data, previousData, loading, refetch } = useBalanceSheetQuery({
    fetchPolicy: 'network-only',
    variables: { householdID: householdId },
    onError: (err) => {
      reportError('error loading balance sheet', err);
      showFeedback(
        `We weren't able to load data for the balance sheet. Please refresh the page to try again.`
      );
    },
  });

  // Fall back to previousData during refetches so the table shows stale data
  // instead of flashing a loading skeleton (e.g. after config form submission).
  const effectiveData = data ?? previousData;

  const householdNode =
    effectiveData?.node?.__typename === 'Household' ? effectiveData.node : null;
  const viewConfig = householdNode?.balanceSheetViewConfiguration ?? null;
  const householdDisplayName = householdNode?.displayName ?? '';

  return (
    <BalanceSheetTableProvider viewConfiguration={viewConfig} loading={loading}>
      <Stack flex={1} minHeight={0}>
        <Stack p={0} flex={1} minHeight={0}>
          <ClientBalanceSheetPageInner
            loading={loading}
            data={effectiveData}
            refetch={refetch}
            householdDisplayName={householdDisplayName}
          />
        </Stack>
      </Stack>
    </BalanceSheetTableProvider>
  );
}
